import {
    isTodoFolderDescriptor,
    isTodoMyDayDescriptor,
    isTodoDueDateDescriptor,
} from '../typeGuards';
import { TodoDescriptor } from '../../schema/TodoDescriptor';
import { TodoDescriptorId } from '../../schema/TodoDescriptorId';
import { TodoDueDateDescriptor } from '../../schema/TodoDueDateDescriptor';
import { TodoFolderDescriptor } from '../../schema/TodoFolderDescriptor';
import { TodoMyDayDescriptor } from '../../schema/TodoMyDayDescriptor';
import { assertNever } from 'owa-assert';

/**
 * This function generates the descriptorId which can be used to
 * uniquely identify a descriptor. It is needed to insert items into the cache.
 * @param descriptor descriptor
 */
export function getDescriptorId(descriptor: TodoDescriptor): string {
    return [getTodoBaseDescriptorId(descriptor), getDescriptorIdForType(descriptor)].join(
        ','
    ) as TodoDescriptorId;
}

function getDescriptorIdForType(descriptor: TodoDescriptor): string {
    if (isTodoFolderDescriptor(descriptor)) {
        return getTodoFolderDescriptorId(descriptor);
    } else if (isTodoDueDateDescriptor(descriptor)) {
        return getTodoDueDateDescriptorId(descriptor);
    } else if (isTodoMyDayDescriptor(descriptor)) {
        return getTodoMyDayDescriptorId(descriptor);
    }
    return assertNever(descriptor);
}

function getTodoBaseDescriptorId(descriptor: TodoDescriptor): TodoDescriptorId {
    const describers = [descriptor.type.toString(), descriptor.isComplete?.toString()];

    // We fetch all the items in a task list unless a maxCount is specified. Because of this,
    // requests made with different OrderByValues result in the same tasks, unless maxCount is specified.
    // For this reason, requests with no maxCount that differ only in OrderByValue should be regarded as the same request
    // (and have the same ID). Requests that have a maxCount should take the OrderByValue into account when calculating their IDs.
    const taskQuantityString = descriptor.maxCount
        ? [descriptor.maxCount, descriptor.orderBy].join(',')
        : undefined;
    if (taskQuantityString) {
        describers.push(taskQuantityString);
    }
    return describers.join(',') as TodoDescriptorId;
}

function getTodoFolderDescriptorId(descriptor: TodoFolderDescriptor): TodoDescriptorId {
    return [descriptor.folderId].join(',') as TodoDescriptorId;
}

function getTodoDueDateDescriptorId(descriptor: TodoDueDateDescriptor): TodoDescriptorId {
    return [descriptor.startDate, descriptor.endDate].join(',') as TodoDescriptorId;
}

function getTodoMyDayDescriptorId(descriptor: TodoMyDayDescriptor): TodoDescriptorId {
    return [descriptor.date].join(',') as TodoDescriptorId;
}
